<template>
  <v-container fluid>
    <page-title icon="fas fa-fw fa-chart-line">
      Metabase Users
      <template v-slot:buttons>
        <v-switch v-model="showInactiveUsers"
                  :label="showInactiveUsers ? 'Show inactive users' : 'Hide inactive users'"
                  dense>
        </v-switch>
      </template>
    </page-title>

    <v-progress-circular v-if="busy"
                         :size="50"
                         indeterminate>
    </v-progress-circular>
    <template v-else>
      <v-form :disabled="canAddUser === false"
              @submit.prevent="createUser">
        <v-card class="mb-4">
          <v-card-title v-if="seatsAvailable > 0">
            You have {{ seatsAvailable }} Metabase {{ seatsAvailable | pluralize('seat', false) }} available.
          </v-card-title>
          <v-alert v-else
                   type="warning"
                   tile
                   class="mb-0">
            You do not have any Metabase seats available.
          </v-alert>
          <v-card-text class="pb-0">
            <p>
              {{ seatsInUse }} / {{ team.max_metabase_users }} Metabase {{ team.max_metabase_users | pluralize('seat', false) }} {{ team.max_metabase_users === 1 ? 'is' : 'are' }} in use.
            </p>

            <template v-if="canAddUser">
              <v-autocomplete v-model="form.user_id"
                              label="Add a user to Metabase"
                              :items="nonMetabaseUsers"
                              item-value="id"
                              :item-text="nameAndEmail"
                              clearable
                              required
                              outlined
                              dense
                              :required="[rules.required]"
                              :error-messages="form.errors.get('user_id')">
              </v-autocomplete>
            </template>
          </v-card-text>
          <v-card-actions v-if="canAddUser && form.user_id"
                          class="px-4">
            <v-btn type="submit"
                   color="primary"
                   :disabled="form.busy">
              Add user to Metabase
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-form>

      <MetabaseUserCard v-for="metabaseUser in filteredUsers"
                        :key="metabaseUser.id"
                        :groups="metabaseGroups"
                        :user="metabaseUser"
                        :seats-available="seatsAvailable"
                        @updated="fetchData">
      </MetabaseUserCard>
    </template>

  </v-container>
</template>
<script>
import metabaseService from '../../services/metabaseService';
import {orderBy} from 'lodash';
import {Form} from 'vform';
import {mapGetters} from 'vuex';
import formValidation from '../../plugins/formValidation';
import MetabaseUserCard from './components/MetabaseUserCard.vue';
import DOMPurify from 'dompurify';
import PageTitle from '../../components/PageTitle.vue';

export default {
  mixins: [formValidation],
  components: {PageTitle, MetabaseUserCard},
  data() {
    return {
      busy: false,
      form: new Form({
        user_id: null,
      }),
      metabaseGroups: [],
      metabaseUsers: [],
      showInactiveUsers: false, // MLD-7142
    }
  },
  computed: {
    ...mapGetters([
      'team',
      'users',
    ]),
    filteredUsers() {
      return this.metabaseUsers.filter(u => {
        if (this.showInactiveUsers) {
          return true;
        }
        return u.mobilelocker_user?.is_active;
      });
    },
    canAddUser() {
      return this.seatsAvailable > 0
    },
    nonMetabaseUsers() {
      return this.users.filter(u => u.is_active && u.is_full_user && this.metabaseUserEmails.includes(u.email) === false);
    },
    metabaseUserEmails() {
      return this.metabaseUsers.map(u => u.email);
    },
    activeUsers() {
      return this.metabaseUsers.filter(u => u.is_active);
    },
    seatsInUse() {
      return this.activeUsers.length;
    },
    seatsAvailable() {
      const seatsAvailable = this.team.max_metabase_users - this.activeUsers.length;
      return Math.max(0, seatsAvailable);
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    nameAndEmail(user) {
      return `${user.name} - ${user.email}`;
    },
    fetchData() {
      this.busy = true;
      return Promise.all([
        metabaseService.getGroupsForTeam()
          .then(data => {
            this.metabaseGroups = orderBy(data, ['name'], ['asc']);
          }),
        metabaseService.getUsersForTeam()
          .then(data => {
            this.metabaseUsers = orderBy(data, ['mobilelocker_user.slug', 'common_name'], ['asc', 'asc']);
          })
      ])
        .catch(() => {
        })
        .finally(() => {
          this.busy = false;
        })
    },
    createUser() {
      if (this.form.busy) {
        return;
      }
      if (this.seatsAvailable === 0) {
        return;
      }
      metabaseService.createUser(this.form)
        .then(data => {
          this.fetchData();
        })
        .catch(err => {
          this.$toast.error(DOMPurify.sanitize(err.response.data.message));
        });
    }
  }
}
</script>
